import { IconChevronLeft12, IconChevronRight12 } from '@cian/ui-kit/icons';
import { IStyleConfig, mergeStyles } from '@cian/utils';
import throttle from 'lodash/throttle';
import * as React from 'react';

import styles from './HorizontalScroll.css';
import { isArrayWithItems } from '../../utils/helpers';

export interface IHorizontalScrollProps extends React.HTMLProps<HTMLDivElement> {
  size: number;
  arrowStyles?: IStyleConfig;
  containerStyle?: IStyleConfig;
  loop?: boolean;
}

const STEP = 170;

interface IHorizontalScrollState {
  marginLeft: number;
  isBusy?: boolean;
}

export class HorizontalScroll extends React.Component<IHorizontalScrollProps, IHorizontalScrollState> {
  private track = React.createRef<HTMLDivElement>();

  public static defaultProps = {
    chidlren: [],
    size: STEP,
  };

  public state = {
    isBusy: false,
    marginLeft: 0,
  };

  public componentDidMount() {
    const { children } = this.props;
    if (isArrayWithItems(children)) {
      this.handleResize();
      window.addEventListener('resize', this.handleResize);
      if (this.track.current) {
        this.track.current.addEventListener('transitionend', this.setNotBusy);
      }
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  public render() {
    const { arrowStyles, children, containerStyle } = this.props;
    const { marginLeft } = this.state;
    if (!isArrayWithItems(children)) {
      return <div />;
    }

    return (
      <div
        {...mergeStyles(
          styles['scroll'],
          this.showLeftArrow && styles['scroll--left'],
          this.showRightArrow && styles['scroll--right'],
          containerStyle,
        )}
      >
        <div
          {...mergeStyles(styles['arrow'], arrowStyles, styles['arrow--right'])}
          onClick={this.handleArrowClick('right')}
        >
          <IconChevronRight12 color="white_100" />
        </div>
        <div
          {...mergeStyles(styles['arrow'], arrowStyles, styles['arrow--left'])}
          onClick={this.handleArrowClick('left')}
        >
          <IconChevronLeft12 color="white_100" />
        </div>
        <div className={styles['scroll-track']} ref={this.track} style={{ marginLeft }}>
          {children}
        </div>
      </div>
    );
  }

  private setNotBusy = () => this.setState({ isBusy: false });

  private handleArrowClick = (to: 'left' | 'right') => () => {
    if (this.state.isBusy) {
      return;
    }
    const marginLeft =
      to === 'left'
        ? this.prevElementOffset
        : this.props.loop && this.trackRightOffset === 0
          ? 0
          : this.nextElementOffset;
    this.setState({ isBusy: true, marginLeft });
  };

  private get showRightArrow() {
    if (this.props.loop) {
      return this.nextElementOffset !== 0;
    }

    return this.trackRightOffset > 0;
  }

  private get showLeftArrow() {
    return this.state.marginLeft < 0;
  }

  private toFirstSlide = () => this.setState({ marginLeft: 0 });

  private handleResize = throttle(this.toFirstSlide, 100);

  private get nextElementOffset() {
    const { marginLeft } = this.state;
    const { size } = this.props;
    const rightOffset = this.trackRightOffset;

    return marginLeft - (rightOffset > size ? size : rightOffset);
  }

  private get prevElementOffset() {
    const { size } = this.props;
    const { marginLeft } = this.state;

    return marginLeft + size > 0 ? 0 : marginLeft + size;
  }

  private get trackRightOffset() {
    if (!this.track.current) {
      return 0;
    }
    const { clientWidth, scrollWidth } = this.track.current;

    return scrollWidth - clientWidth;
  }
}
